class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegated, so it survives every re-render: the in-drawer adds (empty-cart
    // cards, subscribe upgrades) rebuild the whole drawer, overlay included, and
    // a listener bound to the old overlay stopped tap-outside-to-close (2026-09-25).
    this.addEventListener('click', (evt) => {
      if (evt.target.closest && evt.target.closest('#CartDrawer-Overlay')) this.close();
      // Native product navigation also releases the drawer's scroll lock and
      // ?cart=open handoff. Delegation survives quantity/cadence re-renders.
      const productLink = evt.target.closest && evt.target.closest('a.cc-cart__line-thumb');
      if (productLink && !evt.defaultPrevented && evt.button === 0 && !evt.metaKey && !evt.ctrlKey && !evt.shiftKey && !evt.altKey) {
        this.close();
        if (this.classList.contains('active')) evt.preventDefault();
      }
    });
    // Remember where a gesture began, before a replacement drawer can move
    // controls under it. A new intentional press inside the open drawer works
    // immediately; only the opening gesture / same-position double tap is held.
    window.addEventListener('pointerdown', (evt) => this.recordPointerIntent(evt), true);
    window.addEventListener('pointercancel', () => { this.pointerIntent = null; }, true);
    window.addEventListener('click', (evt) => {
      if (this.isOpeningGesture(evt)) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      }
    }, true);
    this.setHeaderCartIconAccessibility();
    // Every re-render (renderContents, the items refresh, a quantity change)
    // swaps the drawer's markup; this writes the heading into whichever empty
    // state lands while the drawer is open, before it paints.
    // The same observer re-applies website experiment wallet-01 to whichever
    // footer lands (a fresh render arrives [hidden]; this runs before paint).
    if (typeof MutationObserver === 'function') {
      new MutationObserver(() => {
        this.fillEmptyState();
        this.applyWalletArm();
        this.maybeExposeWallet();
      }).observe(this, { childList: true, subtree: true });
      new MutationObserver(() => this.syncWalletBusy()).observe(this, { attributes: true, attributeFilter: ['class'] });
    }
    // Wallet taps (wallet-01). Shopify draws the buttons in a closed shadow
    // root, so the click is read from its composed path, on the host elements.
    this.addEventListener('click', (evt) => this.trackWalletTap(evt), true);
  }

  /* Website experiment wallet-01 (Remy, 2026-10-30): A = today's footer,
     B = Shop Pay and Apple Pay, two half-width buttons directly above Protected
     Checkout (snippets/cart-drawer.liquid renders Shopify's block hidden), with
     Route's protection line added ahead of the tap (ensureRouteLine).
     null = not in the test: today's drawer, uncounted. Every step is wrapped: a
     failure leaves today's footer. Contract: site/docs/analytics/experiments.md. */
  walletArm() {
    try {
      const exp = window.antiExp;
      return exp && typeof exp.arm === 'function' ? exp.arm('wallet-01') : null;
    } catch (_) { return null; }
  }

  // Idempotent: in version B only, keeps Shop Pay and Apple Pay and unhides the
  // block; A and null never touch it.
  applyWalletArm() {
    try {
      if (this.walletArm() !== 'B') return;
      this.loadRouteVariants();
      this.querySelectorAll('[data-cc-wallets] shopify-accelerated-checkout-cart[wallet-configs]:not([data-cc-wallets-kept])')
        .forEach((host) => this.keepShopPayAndApplePay(host));
      this.querySelectorAll('[data-cc-wallets][hidden]').forEach((el) => el.removeAttribute('hidden'));
      // A render that shows merchandise and no protection row holds the wallets before
      // it paints; the read that follows decides (routeHold).
      const receipt = this.querySelector('[data-cc-receipt]');
      if (receipt && this.querySelector('.cc-cart__line') && !(Number(receipt.getAttribute('data-route-cents')) > 0)) this.routeKnown = null;
      this.applyWalletHold();
      this.watchRouteQuote();
      this.ensureRouteLine();
    } catch (_) { /* today's footer stays */ }
  }

  /* Route protection before a wallet tap (Remy, 2026-10-30: wallets should carry Route "the
     same as if they do protected checkout"). Route's script adds its protection line only
     when the shopper taps Protected Checkout (the `routeins` variant priced at its quote,
     one unit, property _route=protection), and takes it out on the next page load that
     finds it; a wallet from the drawer pays the cart as it stands. In version B the drawer
     adds that same line itself while it is open, so Shop
     Pay and Apple Pay carry protection. Verified on the draft 2026-10-30: Shop Pay's page
     listed the line, Protected Checkout did not add a second one, and Route's "Checkout
     Without Package Protection" removed it and stamped route_toggle_state=0, which this
     honours. The quote comes from Route's own widget, and only a quote of the cart just
     read is used (routeQuoteIsFresh); the variant comes from Route's product. */
  // What Route will charge: total-amount is the tier price it bills ($6.95 for a
  // $6.78 premium); protect-premium is the raw premium, the fallback. Route marks a
  // cart it does not protect has-protect="false", and full-coverage (paid by the
  // merchant) bills the shopper nothing: no line for either (2026-10-31).
  routeQuoteCents() {
    const widget = this.querySelector('route-cart-preferred');
    const protect = widget ? widget.getAttribute('has-protect') : null;
    if (protect === null || protect === 'false' || widget.hasAttribute('full-coverage')) return null;
    for (const name of ['total-amount', 'protect-premium']) {
      const cents = Math.round(Number(widget.getAttribute(name)) * 100);
      if (Number.isFinite(cents) && cents > 0) return cents;
    }
    return null;
  }

  /* Route's quote names the cart it read: cart-token, and cart-subtotal (that cart's
     items_subtotal_price / 100). Route re-quotes after each cart write by reading
     /cart.js, and two writes close together can hand it the earlier cart: on the draft
     (2026-10-31) the widget quoted a $32.25 cart while the cart held $91.50, the drawer
     added that $2.15 tier, and Route re-tiered it to $3.75 with a second write that
     dimmed the wallets again and redrew the drawer. A quote is used only when it names
     the cart just read (tokens compared without their ?key= part). */
  static subtotalCents(cart) {
    return Number(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price);
  }

  static routeQuoteIsFresh(widget, cart) {
    const base = (token) => String(token == null ? '' : token).split('?')[0];
    const token = base(widget.getAttribute('cart-token'));
    const subtotal = widget.getAttribute('cart-subtotal');
    return !!token && token === base(cart.token) && !!subtotal && !!subtotal.trim() &&
      Math.round(Number(subtotal) * 100) === CartDrawer.subtotalCents(cart);
  }

  /* A stale quote is answered by asking Route for a new one, never by guessing a tier.
     Route re-quotes when it sees a fetch to /cart/update (it watches resource timings):
     writing its own __route_cart_id back unchanged asks for that and moves nothing. The
     drawer's lock does not count it and antiCartState files it as Route metadata.
     /cart.js leaves that attribute out (write responses carry it), so a cart read there
     gets {attributes: {}}, which does the same (verified on the draft 2026-10-31: a new
     quote within about 400 ms). Once per cart state, at most every 3 s; the quote
     watcher runs the sync again when the new quote lands. */
  async nudgeRouteQuote(cart, json) {
    const state = String(cart.token).split('?')[0] + '|' + CartDrawer.subtotalCents(cart);
    if (this.routeNudged === state) return;
    const wait = (this.routeNudgedAt || 0) + 3000 - Date.now();
    if (wait > 0) { this.retryRouteLine(wait); return; }
    this.routeNudged = state;
    this.routeNudgedAt = Date.now();
    const id = (cart.attributes || {}).__route_cart_id;
    await json('cart/update.js', { attributes: typeof id === 'string' && id ? { __route_cart_id: id } : {} });
  }

  // Route's quote arrives after the drawer renders and changes with the cart:
  // each widget is watched once, and each new quote (Route sets these together,
  // the cart it describes included) runs the sync again, as does is-loading clearing.
  watchRouteQuote() {
    if (typeof MutationObserver !== 'function') return;
    this.routeWatched = this.routeWatched || new WeakSet();
    this.querySelectorAll('route-cart-preferred').forEach((widget) => {
      if (this.routeWatched.has(widget)) return;
      this.routeWatched.add(widget);
      new MutationObserver(() => this.ensureRouteLine()).observe(widget, { attributes: true, attributeFilter: ['total-amount', 'protect-premium', 'has-protect', 'cart-subtotal', 'cart-token', 'is-loading'] });
    });
  }

  static isRouteLine(item) {
    return !!item && (item.vendor === 'Route' || item.sku === 'ROUTEINS' || item.handle === 'routeins');
  }

  /* Route's tiers as {price in cents: variant id}, from its routeins product, read once a
     session (sessionStorage anti-route-variants, kept a day) and asked for the first time
     version B renders the drawer, which happens on the add response before any open: the
     table is in hand when the drawer opens and the add does not wait on it (2026-10-31). */
  loadRouteVariants() {
    if (this.routeVariants) return this.routeVariants;
    const key = 'anti-route-variants';
    const tier = (price, id) => Number.isInteger(price) && price > 0 && Number.isSafeInteger(id) && id > 0;
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      const age = saved && typeof saved.date === 'string' ? Date.now() - Date.parse(saved.date) : NaN;
      if (age >= 0 && age < 864e5 && saved.prices && typeof saved.prices === 'object') {
        const table = {};
        for (const [price, id] of Object.entries(saved.prices)) if (tier(Number(price), Number(id))) table[Number(price)] = Number(id);
        if (Object.keys(table).length) return (this.routeVariants = Promise.resolve(table));
      }
    } catch (_) { /* read Route's product instead */ }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.routeVariants = fetch(root + 'products/routeins.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        const table = {};
        for (const v of (p && Array.isArray(p.variants) ? p.variants : [])) {
          const price = Number(v && v.price), id = Number(v && v.id);
          if (v && v.available !== false && tier(price, id) && !(price in table)) table[price] = id;
        }
        if (Object.keys(table).length) {
          try { window.sessionStorage.setItem(key, JSON.stringify({ date: new Date().toISOString(), prices: table })); } catch (_) { /* this page keeps it */ }
        }
        return table;
      })
      .catch(() => { this.routeVariants = null; return {}; });
    return this.routeVariants;
  }

  async routeVariantFor(cents) {
    const table = await this.loadRouteVariants();
    if (table[cents]) return table[cents];
    // Route bills the next tier up from a raw premium; the nearest tier above
    // the quote, and only a close one (its steps are 20 to 65 cents).
    const above = Object.keys(table).map(Number).filter((price) => price > cents).sort((a, b) => a - b)[0];
    return above !== undefined && above - cents <= 70 ? table[above] : null;
  }

  /* The line lives only while the drawer is open. Route re-tiers its line on every
     cart change, and that write lands in the middle of other flows' cart reads
     (the post-add offer failed with "Cart changed while it was being read" on
     Remy's phone, 2026-10-30). A wallet or Protected Checkout is tapped with the
     drawer open, so the line is there when it matters and gone when the drawer
     closes; the next open adds it again. Route's widget also takes the line out, but
     only on a page load, by removing it and reloading the page (its removeRouteOnLoad;
     a session flag spares the load right after that reload). Seen on the draft
     2026-10-31: a reload with the line in the cart wrote {updates: {line: 0}} and
     reloaded. The drop on close spares the shopper that reload. */
  async dropRouteLine(attempt = 0) {
    try {
      if (this.walletArm() !== 'B' || document.documentElement.classList.contains('cc-cart-open')) return;
      if (this.classList.contains('cc-cart--busy') || this.routeSyncing) {
        if (attempt < 8) setTimeout(() => this.dropRouteLine(attempt + 1), 400);
        return;
      }
    } catch (_) { return; }
    this.routeSyncing = true;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const json = (path, body) => fetch(root + path, body
        ? { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
        : { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => (r.ok ? r.json() : Promise.reject(new Error('cart'))));
      const state = window.antiCartState;
      if (state && typeof state.whenIdle === 'function') await state.whenIdle();
      const cart = (state && typeof state.peek === 'function' && state.peek()) || await json('cart.js');
      if (!cart || !Array.isArray(cart.items)) return;
      const sections = this.getSectionsToRender().map((section) => section.id).join(',');
      let last = null;
      for (const line of cart.items.filter(CartDrawer.isRouteLine)) last = await json('cart/change.js', { id: line.key, quantity: 0, sections });
      if (last && last.sections && typeof this.renderContents === 'function') {
        try { this.renderContents(last, { open: false }); } catch (_) { /* the next render catches up */ }
      }
    } catch (_) { /* the line stays: the next open finds it, or Route removes it on the next page load */ }
    finally { this.routeSyncing = false; }
  }

  // One pending re-run at a time; a burst of triggers collapses into one pass.
  retryRouteLine(delay) {
    if (this.routeTimer) return;
    this.routeTimer = setTimeout(() => { this.routeTimer = null; this.ensureRouteLine(); }, delay);
  }

  /* One hold that follows the cart (Remy, 2026-10-31: greyed out for a second is fine).
     The wallets are held, inert and dimmed, whenever the drawer is open in version B
     with a wallet block and Route's line is not confirmed in the cart. Confirmed means
     the last cart read, or our add's response, showed the line and no line-changing
     write has started since (window.ccCartMutatedAt, stamped by the drawer's lock), so
     every cart change holds them until a read after it shows the line again. Remy's
     iPhone, same day: a product deleted (Route then drops its lone line, with no
     redraw), another added, and the Apple Pay sheet showed no protection: the drawer,
     held only once per open, had gone live during the re-add. Released as well when
     Route does not apply (its opt-out flag, has-protect="false", full coverage, no
     merchandise).
     Each hold has its own 4 s cap (Protected Checkout waits 4 s for Route), restarted
     by every cart change. Closed, the look is left as it is; the next open decides. */
  noteRoute(state, mark) {
    this.routeKnown = { state, mark };
  }

  routeHold() {
    let episode = this.holdEpisode;
    const drop = () => { if (episode) { clearTimeout(episode.timer); this.holdEpisode = null; } };
    if (!document.documentElement.classList.contains('cc-cart-open')) { drop(); return !!this.walletSettling; }
    const known = this.routeKnown, mark = window.ccCartMutatedAt;
    if (!this.querySelector('[data-cc-wallets]') || (known && known.mark === mark && (known.state === 'in' || known.state === 'na'))) { drop(); return false; }
    if (!episode || episode.open !== this.openGeneration || episode.mark !== mark) {
      drop();
      episode = this.holdEpisode = { open: this.openGeneration, mark, capped: false };
      episode.timer = setTimeout(() => {
        if (this.holdEpisode !== episode) return;
        episode.capped = true;
        this.applyWalletHold();
      }, 4000);
    }
    return !episode.capped;
  }

  async ensureRouteLine() {
    try {
      if (this.walletArm() !== 'B' || !this.querySelector('[data-cc-wallets]')) return;
      if (!document.documentElement.classList.contains('cc-cart-open')) return;
      this.applyWalletHold();
      // The post-add dialog owns the cart while it is open (its offer reads and
      // writes the cart in steps that a write of ours would invalidate: "Cart
      // changed while it was being read"); the drawer's own lock and a sync
      // already running are waited out too. Each of these runs once more later.
      const dialog = document.querySelector('[data-cc-pdpup]');
      if (dialog && !dialog.hidden) { this.routeRetry = true; return; }
      if (this.classList.contains('cc-cart--busy') || this.routeSyncing) { this.routeRetry = true; return; }
      // A cart write by anyone in the last moment (a stepper tap, a cadence change,
      // the offer) gets its follow-up reads done before this adds to the queue.
      const now = window.performance && performance.now ? performance.now() : Date.now();
      const since = now - window.ccCartMutatedAt;
      if (Number.isFinite(since) && since < 800) { this.retryRouteLine(Math.ceil(800 - since)); return; }
    } catch (_) { return; }
    this.routeSyncing = true;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const json = (path, body) => fetch(root + path, body
        ? { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
        : { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => (r.ok ? r.json() : Promise.reject(new Error('cart'))));
      const state = window.antiCartState;
      if (state && typeof state.whenIdle === 'function') await state.whenIdle();
      const mark = window.ccCartMutatedAt; // what this read can confirm: the cart as of the last counted write
      const cart = (state && typeof state.peek === 'function' && state.peek()) || await json('cart.js');
      if (!cart || !Array.isArray(cart.items)) return;
      // The shopper declined protection (Route's own flag): no line, nothing to wait for.
      if (String((cart.attributes || {}).route_toggle_state) === '0') { this.noteRoute('na', mark); return; }
      const routeLines = cart.items.filter(CartDrawer.isRouteLine);
      const merchandise = cart.items.filter((item) => !CartDrawer.isRouteLine(item));
      // Every write asks for the drawer's section too, so the drawer is redrawn
      // from the cart it just made: an emptied cart shows its empty card at once.
      const sections = this.getSectionsToRender().map((section) => section.id).join(',');
      let last = null;
      if (!merchandise.length) {
        this.noteRoute('na', mark);
        for (const line of routeLines) last = await json('cart/change.js', { id: line.key, quantity: 0, sections });
      } else if (routeLines.length) {
        // Once its line is in the cart Route's own widget re-tiers it on every cart
        // change (seen on the draft: a $6.95 line against a $6.78 quote), so the
        // drawer only ever adds a missing line and never swaps one.
        this.noteRoute('in', mark);
      } else {
        this.noteRoute('missing', mark);
        const widget = this.querySelector('route-cart-preferred');
        // Route's quote step can still remove a line (its lone-line removal runs while it
        // quotes): no add while it carries is-loading; the watcher runs this when it clears.
        if (!widget || widget.hasAttribute('is-loading')) return;
        if (!CartDrawer.routeQuoteIsFresh(widget, cart)) { await this.nudgeRouteQuote(cart, json); return; }
        if (widget.getAttribute('has-protect') === 'false' || widget.hasAttribute('full-coverage')) { this.noteRoute('na', mark); return; }
        const cents = this.routeQuoteCents();
        if (cents === null) return;
        const variantId = await this.routeVariantFor(cents);
        if (!variantId) return;
        const adding = json('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: { _route: 'protection' } }], sections });
        const addMark = window.ccCartMutatedAt; // the lock stamps our own add as it starts
        last = await adding;
        this.noteRoute('in', addMark);
      }
      if (last && last.sections && typeof this.renderContents === 'function') {
        try { this.renderContents(last, { open: false }); } catch (_) { /* the next render catches up */ }
      }
    } catch (_) { /* the cart stays as it was; Protected Checkout still adds protection */ }
    finally {
      this.routeSyncing = false;
      this.applyWalletHold();
      if (this.routeRetry) { this.routeRetry = false; this.retryRouteLine(300); }
    }
  }

  /* Shopify's cart host lists every wallet the store has turned on in its
     wallet-configs attribute (here Shop Pay, Amazon Pay, PayPal, Google Pay and,
     on Apple devices, Apple Pay) and reads that list once, when it connects.
     Remy (2026-10-30): "just shop pay and Apple Pay ... two half-width buttons".
     A fresh host carrying only those two, put in the first one's place, is read
     by Shopify as a two-wallet cart and drawn as one row (Shop Pay alone where
     Apple Pay is not offered). The marker keeps this to one pass per render. A
     list that cannot be read, or holds neither wallet, is left as Shopify sent it.
     Every drawer render brings a new host from the server and Shopify redraws its
     buttons; keeping the drawn host across renders was tried on 2026-10-31 and saved
     nothing (Shopify redraws inside its closed shadow root on reconnect anyway), so
     each render is cut afresh and the number of renders is what is kept small. */
  keepShopPayAndApplePay(host) {
    let configs;
    try { configs = JSON.parse(host.getAttribute('wallet-configs')); } catch (_) { return; }
    if (!Array.isArray(configs)) return;
    const kept = configs.filter((c) => c && (c.name === 'shop_pay' || c.name === 'apple_pay'));
    if (!kept.length || kept.length === configs.length) { host.setAttribute('data-cc-wallets-kept', kept.length ? 'all' : 'none'); return; }
    const fresh = document.createElement(host.tagName.toLowerCase());
    for (const attr of host.attributes) fresh.setAttribute(attr.name, attr.value);
    fresh.setAttribute('wallet-configs', JSON.stringify(kept));
    fresh.setAttribute('data-cc-wallets-kept', String(kept.length));
    host.replaceWith(fresh);
  }

  // While a cart change is in flight (the inline lock's cc-cart--busy), the
  // wallets are inert: a wallet must never pay for the cart before the change.
  // Version B also holds them while Route's line is not confirmed (routeHold), in
  // the same look; the marker drops the fade going in. Every release of the lock
  // runs the Route sync, which reads the cart the change left.
  syncWalletBusy() {
    try {
      const busy = this.classList.contains('cc-cart--busy');
      const released = this.lockBusy && !busy;
      this.lockBusy = busy;
      this.applyWalletHold();
      if (released || (!busy && this.routeRetry)) { this.routeRetry = false; this.ensureRouteLine(); }
    } catch (_) { /* nothing to sync */ }
  }

  applyWalletHold() {
    try {
      const busy = this.classList.contains('cc-cart--busy');
      if (this.walletArm() === 'B') this.walletSettling = this.routeHold();
      const settling = !!this.walletSettling;
      this.querySelectorAll('[data-cc-wallets]').forEach((el) => {
        if (busy || settling) {
          if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
          if (el.getAttribute('aria-busy') !== 'true') el.setAttribute('aria-busy', 'true');
        } else {
          if (el.hasAttribute('inert')) el.removeAttribute('inert');
          if (el.hasAttribute('aria-busy')) el.removeAttribute('aria-busy');
        }
        if (el.hasAttribute('data-cc-wallets-settling') !== settling) el.toggleAttribute('data-cc-wallets-settling', settling);
      });
    } catch (_) { /* nothing to hold */ }
  }

  // Once per page load, both versions: the first time the drawer is open with
  // a line (and the wallet block rendered, the same Liquid for A and B).
  maybeExposeWallet() {
    try {
      if (this.walletExposed || !document.documentElement.classList.contains('cc-cart-open')) return;
      if (!this.querySelector('.cc-cart__line') || !this.querySelector('[data-cc-wallets]')) return;
      if (this.walletArm() == null) return;
      this.walletExposed = true;
      // Subscription lines are grouped as .cc-cart__line--sub and carry the
      // cadence select (Liquid: item.selling_plan_allocation != null).
      const mode = this.querySelector('.cc-cart__line--sub, [data-cart-cadence]') ? 'subscription' : 'one_time';
      window.antiExp.expose('wallet-01', { surface: 'cart_drawer', mode });
    } catch (_) { /* uncounted, never broken */ }
  }

  trackWalletTap(evt) {
    try {
      const path = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
      if (!path.some((node) => node && node.hasAttribute && node.hasAttribute('data-cc-wallets'))) return;
      const names = {
        'shop-pay-wallet-button': 'cart.wallet_shop_pay',
        'shopify-apple-pay-button': 'cart.wallet_apple_pay',
        'shopify-google-pay-button': 'cart.wallet_google_pay'
      };
      let name = null;
      for (const node of path) {
        const tag = node && node.tagName ? node.tagName.toLowerCase() : '';
        if (names[tag]) { name = names[tag]; break; }
        if (/^(shop|shopify)-[a-z-]+-button$/.test(tag)) { name = 'cart.wallet_other'; break; }
        if (node && node.hasAttribute && node.hasAttribute('data-cc-wallets')) break;
      }
      if (name && window.antiExp && typeof window.antiExp.track === 'function') window.antiExp.track(name);
    } catch (_) { /* tracking never blocks a wallet */ }
  }

  // The empty cart's heading is written here, only while the drawer is open,
  // and never served in the page HTML (2026-10-14). The drawer is rendered
  // into every page, and an AI reading /pages/trust-center took the heading
  // for the page's own content and called the page empty. The words are
  // unchanged; the snippet's <h2 data-cc-empty-title> is left empty for them.
  fillEmptyState() {
    if (!document.documentElement.classList.contains('cc-cart-open')) return;
    const text = 'Nothing in here yet.';
    this.querySelectorAll('.cc-cart__empty-title[data-cc-empty-title]').forEach((title) => {
      if (title.textContent !== text) title.textContent = text;
    });
  }

  recordPointerIntent(event) {
    if (!event.isTrusted || (event.button != null && event.button !== 0)) return;
    this.pointerIntent = {
      inside: document.documentElement.classList.contains('cc-cart-open') && this.contains(event.target),
      generation: this.openGeneration || 0, x: event.clientX, y: event.clientY,
      type: event.pointerType, at: Date.now()
    };
  }

  isOpeningGesture(event) {
    if (!event.isTrusted || !event.detail || !this.contains(event.target) || Date.now() - (this.openedAt || 0) >= 400) return false;
    const intent = this.pointerIntent;
    if (!intent || !intent.inside || intent.generation !== this.openGeneration) return true;
    const opening = this.openingPointer;
    const samePosition = opening && intent.at - opening.at < 400 && Number.isFinite(opening.x) && Number.isFinite(opening.y) &&
      Math.abs(intent.x - opening.x) <= 16 && Math.abs(intent.y - opening.y) <= 16;
    // Touch click counts are inconsistent after the DOM changes. Geometry
    // catches that repeat tap while a newly aimed checkout press goes through.
    return !!(samePosition && (event.detail > 1 || intent.type === 'touch'));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Tile geometry + page condense (Remy 2026-08-28). Both values must be
    // measured at open time, before the body gets pinned:
    //  - --cc-cart-anchor: the floating menu bar's live TOP edge — the tile
    //    lands ON the bar, replacing that module (Remy, round 2), so it tracks
    //    wherever the bar actually is (its `top` is scroll-state-dependent,
    //    inline-set by syncTop). Floored at 8px for the scrolled-down state
    //    where the bar has translated itself off-screen and rect.top goes
    //    negative — the tile still lands just inside the top edge.
    //  - --cc-condense-oy: the viewport's centre in .cc-page-canvas
    //    coordinates, so the 98% scale breathes around what the user is
    //    looking at instead of sliding a long page toward its middle.
    //
    // Both measurements are taken BEFORE either write, and each value is set on
    // its only consumer (2026-09-22 speed audit): --cc-cart-anchor on this
    // element (read by the panel inside it), --cc-condense-oy on the canvas.
    // Writing them onto <html> between the two rect reads restyled the whole
    // document inside the tap handler. Neither write moves the other element,
    // so the values are exactly what they were.
    const docEl = document.documentElement;
    const header = document.querySelector('.antilabs-header');
    const canvas = document.querySelector('.cc-page-canvas');
    const anchorTop = header ? Math.max(header.getBoundingClientRect().top, 8) : null;
    const condenseOy = canvas ? window.innerHeight / 2 - canvas.getBoundingClientRect().top : null;
    if (anchorTop !== null) this.style.setProperty('--cc-cart-anchor', `${anchorTop}px`);
    if (condenseOy !== null) canvas.style.setProperty('--cc-condense-oy', `${condenseOy}px`);
    if (!docEl.classList.contains('cc-cart-open')) {
      this.openedAt = Date.now();
      this.openGeneration = (this.openGeneration || 0) + 1;
      this.openingPointer = this.pointerIntent;
    }
    docEl.classList.add('cc-cart-open');
    this.fillEmptyState();
    this.applyWalletArm();
    this.maybeExposeWallet();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockBodyScroll();
  }

  close() {
    if (window.antiCheckoutFeedback && typeof window.antiCheckoutFeedback.onDrawerClose === 'function' && !window.antiCheckoutFeedback.onDrawerClose()) return;
    this.classList.remove('active');
    // Releases the page condense; the CSS transition animates the canvas back
    // to full scale in step with the tile's slide-out.
    document.documentElement.classList.remove('cc-cart-open');
    setTimeout(() => this.dropRouteLine(), 0); // wallet-01 B: Route's line lives only while the drawer is open
    removeTrapFocus(this.activeElement);
    this.unlockBodyScroll();
    // The ?cart=open handoff (below) leaves with the drawer, not on arrival.
    if (new URLSearchParams(location.search).has('cart')) {
      const u = new URL(location);
      u.searchParams.delete('cart');
      history.replaceState({}, '', u);
    }
  }

  // iOS-safe body scroll lock. `overflow: hidden` on <body> alone does NOT
  // stop touch scrolling in iOS Safari — the page keeps scrolling behind the
  // fixed drawer, which reads to the user as "the cart won't scroll / does
  // nothing." We pin <body> with position: fixed (via the .cc-scroll-locked
  // class) at its current scroll offset, then restore that offset on close so
  // the page doesn't jump. The .overflow-hidden class is kept so any CSS still
  // keyed on it continues to apply. `top` is the only dynamic value (the saved
  // scroll offset), so it's set inline here; the static pinning lives in CSS.
  lockBodyScroll() {
    if (document.body.classList.contains('cc-scroll-locked')) return;
    this.scrollLockTop = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.top = `-${this.scrollLockTop}px`;
    document.body.classList.add('overflow-hidden', 'cc-scroll-locked');
  }

  unlockBodyScroll() {
    if (!document.body.classList.contains('cc-scroll-locked')) return;
    document.body.classList.remove('overflow-hidden', 'cc-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollLockTop || 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    this.renderGeneration = (this.renderGeneration || 0) + 1;
    this.preparedRender = null;
    // Stamp the moment a full drawer render happens. CartDrawerItems'
    // cartUpdate handler reads this to de-dupe: an add from a product form
    // publishes cartUpdate AND then synchronously calls renderContents(), so
    // the items component would otherwise re-fetch + re-render the same drawer
    // a second time (a redundant round-trip racing this render).
    CartDrawer.lastRenderAt = (window.performance && performance.now) ? performance.now() : Date.now();
    // Drop the is-empty marker from BOTH the host element and the panel
    // so CSS rules scoped to `cart-drawer.is-empty .drawer__inner` and to
    // `cart-drawer .drawer__inner.is-empty` both release. The host class
    // was previously left stale after add-to-cart, which left the wrong
    // CSS rules winning even though the cart had items.
    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    const items = this.querySelector('cart-drawer-items');
    if (items && items.classList.contains('is-empty')) items.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const newInnerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (newInnerHTML != null) sectionElement.innerHTML = newInnerHTML;
    });
    // A fresh fallback can observe a cart cleared elsewhere after the add.
    // Mirror the server panel's empty state just as onCartUpdate does.
    const renderedInner = this.querySelector('.drawer__inner');
    if (renderedInner) this.classList.toggle('is-empty', renderedInner.classList.contains('is-empty'));

    if (options.open !== false) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  // Prepare server markup while the offer is visible. Never display it early,
  // or keep it across an unconfirmed write. An in-flight preparation is shared
  // with the eventual handoff, avoiding a second request after decline.
  isPreparedContentsCurrent(prepared) {
    const cartState = window.antiCartState;
    if (!cartState || !prepared || typeof prepared.preparedAt !== 'number' || Date.now() - prepared.preparedAt > 30000) return false;
    if (cartState.isCurrent(prepared.revision)) return true;
    // Route's cart-link/returns attributes do not render into the drawer. The
    // service can prove every other field identical from a confirmed full cart;
    // it returns no key during a write or when that snapshot is unavailable.
    // History proves the exact original revision, including confirmations that
    // arrive after sections. Its lifecycle reset must override a stored key.
    const presentationKey = typeof cartState.presentationKeyFor === 'function'
      ? cartState.presentationKeyFor(prepared.revision) : prepared.presentationKey;
    return !!presentationKey && typeof cartState.presentationKey === 'function' &&
      presentationKey === cartState.presentationKey();
  }

  prepareContents(options = {}) {
    const cartState = window.antiCartState;
    const ids = this.getSectionsToRender().map((section) => section.id);
    const existing = this.sectionPreparation;
    if (cartState && existing && (!existing.ready || this.isPreparedContentsCurrent(existing))) {
      return existing.promise;
    }
    const preparation = { ready: false, revision: null, promise: null };
    const generation = this.renderGeneration || 0;
    this.sectionPreparation = preparation;
    const refresh = () => {
      const idle = cartState && typeof cartState.whenPresentationIdle === 'function'
        ? cartState.whenPresentationIdle()
        : cartState && typeof cartState.whenIdle === 'function' ? cartState.whenIdle() : Promise.resolve(null);
      return idle.then((revision) => {
        preparation.revision = revision;
        const rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
        let timeout;
        // Bound both headers and body parsing. The promise guard also works
        // without AbortController, and a late reply cannot reach the renderer.
        const request = new Promise((resolve, reject) => {
          timeout = setTimeout(() => {
            reject(new Error('Cart sections timed out'));
            if (controller) controller.abort();
          }, 10000);
          fetch(rootUrl + '?sections=' + ids.join(','), controller ? { signal: controller.signal } : {})
            .then((response) => {
              if (!response.ok) throw new Error('Could not refresh cart');
              return response.json();
            }).then(resolve, reject);
        }).finally(() => clearTimeout(timeout));
        return request
          .then((sections) => {
            // A mutation can start while the section request is in flight.
            // Wait for it and read again instead of painting its old receipt.
            if (cartState && typeof revision === 'number' && cartState.revision() !== revision) return refresh();
            if (!ids.length || !sections || !ids.every((id) => typeof sections[id] === 'string' && sections[id].length > 0)) throw new Error('Cart sections unavailable');
            const result = { sections: sections };
            if (typeof revision === 'number') Object.defineProperty(result, '__antiCartRevision', { value: revision });
            const presentationKey = cartState && cartState.isCurrent(revision) && typeof cartState.presentationKey === 'function'
              ? cartState.presentationKey() : null;
            Object.defineProperty(result, '__antiCartPreparedAt', { value: Date.now() });
            Object.defineProperty(result, '__antiCartPresentationKey', { value: presentationKey });
            return result;
          });
      });
    };
    preparation.promise = refresh().then((result) => {
      preparation.ready = true;
      preparation.preparedAt = result.__antiCartPreparedAt;
      preparation.presentationKey = result.__antiCartPresentationKey;
      // The prior cartUpdate subscriber also refreshed the closed drawer.
      // Retain that Route warmup, but never open/focus it or overwrite a newer
      // render. The eventual handoff can open this exact mounted DOM.
      if (options.renderHidden && cartState && cartState.isCurrent(result.__antiCartRevision) &&
          !document.documentElement.classList.contains('cc-cart-open') && (this.renderGeneration || 0) === generation) {
        this.renderContents(result, { open: false });
        this.preparedRender = {
          revision: result.__antiCartRevision, generation: this.renderGeneration,
          preparedAt: result.__antiCartPreparedAt, presentationKey: result.__antiCartPresentationKey
        };
      }
      return result;
    }).catch((error) => {
      if (this.sectionPreparation === preparation) this.sectionPreparation = null;
      throw error;
    });
    return preparation.promise;
  }

  // A bundled response may wait behind the offer for an arbitrary length of
  // time. Reuse it only while the cart service can prove no write superseded
  // it; a missing service or ambiguous write uses a fresh server render.
  renderConfirmedContents(parsedState) {
    const cartState = window.antiCartState;
    const ids = this.getSectionsToRender().map((section) => section.id);
    const openPrepared = () => {
      const prepared = this.preparedRender;
      if (!(this.isPreparedContentsCurrent(prepared) && prepared.generation === this.renderGeneration)) return false;
      this.open();
      return true;
    };
    if (openPrepared()) return Promise.resolve();
    if (cartState && parsedState && cartState.isCurrent(parsedState.__antiCartRevision) && parsedState.sections && ids.length && ids.every((id) => typeof parsedState.sections[id] === 'string' && parsedState.sections[id].length > 0)) {
      try {
        this.renderContents(parsedState);
        return Promise.resolve();
      } catch (_) { /* Fall through to a fresh render on malformed markup. */ }
    }
    const shared = this.sectionPreparation;
    const preparing = this.prepareContents();
    return preparing.catch((error) => {
      // A decline can join a background request immediately before it fails.
      // Discard that shared attempt and make the same fresh fallback that a
      // later decline would have used; do not loop on a failing fresh read.
      if (shared && shared.promise === preparing) return this.prepareContents();
      throw error;
    }).then((response) => {
      const prepared = { revision: response.__antiCartRevision, preparedAt: response.__antiCartPreparedAt, presentationKey: response.__antiCartPresentationKey };
      if (cartState && cartState.revision() !== response.__antiCartRevision && !this.isPreparedContentsCurrent(prepared)) return this.renderConfirmedContents(null);
      if (openPrepared()) return;
      this.renderContents(response);
    });
  }

  // A cadence-only change usually leaves the receipt unchanged. Destroying
  // its hydrated Route widget makes the footer collapse and remount from below.
  // The server key covers every rendered receipt input: reuse only on equality;
  // a changed price, discount, shipping rate, tax mode or delivery date falls
  // through to the normal complete refresh. Never detach the retained footer.
  renderCadenceContents(target, html) {
    const source = document.createElement('div');
    source.innerHTML = html;
    const currentFooter = target.querySelector('.cc-cart__foot');
    const nextFooter = source.querySelector('.cc-cart__foot');
    const key = currentFooter?.dataset.ccFooterKey;
    if (!key || key !== nextFooter?.dataset.ccFooterKey || currentFooter.parentElement !== target) return false;
    nextFooter.remove();
    Array.from(target.childNodes).forEach((node) => {
      if (node !== currentFooter) node.remove();
    });
    const fragment = document.createDocumentFragment();
    while (source.firstChild) fragment.appendChild(source.firstChild);
    target.insertBefore(fragment, currentFooter);
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  getSectionsToRender() {
    // cart-icon-bubble is intentionally NOT requested here: it is not a
    // section in this theme (the header count badge is an inline element
    // kept in sync by custom-header.liquid's own /cart/* fetch hook).
    // Requesting a nonexistent section returns empty HTML and made the
    // render loop throw.
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ?cart=open — the /cart page's handoff (checkout's bag icon lands there;
   Aaron 2026-09-01 wants everyone in THIS drawer). Open once the element is
   ready. The param stays until the drawer closes (close() strips it), so
   refresh/share still don't re-open a closed cart. It used to go on arrival,
   which lost the drawer to Route: back from a protected checkout, Route's
   widget drops its protection line from the cart and RELOADS the page, and
   the stripped URL came back on the home page with the cart shut
   (2026-09-24, open-items E10; legacy widget and Zippy alike). */
if (new URLSearchParams(location.search).has('cart')) {
  window.addEventListener('DOMContentLoaded', function () {
    var d = document.querySelector('cart-drawer');
    if (d && typeof d.open === 'function') d.open();
  });
}

class CartDrawerItems extends CartItems {
  // Defer + de-dupe the cartUpdate reaction so a product-form add doesn't
  // render the drawer twice. The add-forms (PDP hero, bundle section)
  // publish cartUpdate and then SYNCHRONOUSLY call cart.renderContents(),
  // which fully re-renders + opens the drawer. The inherited handler reacted
  // to that same publish by ALSO fetching ?section_id=cart-drawer and
  // replacing the DOM — a second, redundant network round-trip racing the
  // renderContents render (the "feels slow / flickery on add" symptom).
  //
  // We defer our reaction by one microtask: renderContents() runs right after
  // the synchronous publish and stamps CartDrawer.lastRenderAt, so we can tell
  // the update was already painted and skip the re-fetch. Updates NOT paired
  // with a renderContents() (e.g. an external cart mutation while the drawer
  // is open) leave lastRenderAt stale, so they fall through and still refresh.
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // The hero owns its full add → offer → drawer handoff, including all
      // fresh-render fallbacks. A parallel subscriber fetch only duplicates it.
      if (event.source === 'cart-items' || event.drawerHandled === true) return;
      Promise.resolve().then(() => {
        const now = (window.performance && performance.now) ? performance.now() : Date.now();
        if (now - (CartDrawer.lastRenderAt || -Infinity) < 500) return;
        this.onCartUpdate();
      });
    });
  }

  // Refresh the whole panel from the server. The inherited version swapped
  // cart-drawer-items and '.cart-drawer__footer', a class this drawer does not
  // have, so totals, savings and the shipping bar stayed stale; and a response
  // that set off before a newer cart change or render could land after it and
  // put the old lines back. Such a response is now dropped (2026-09-25).
  onCartUpdate(options = {}) {
    const stamp = () => ((window.performance && performance.now) ? performance.now() : Date.now());
    const started = stamp();
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((text) => {
        const overtaken = (CartDrawer.lastRenderAt || -Infinity) > started || (window.ccCartMutatedAt || -Infinity) > started;
        if (overtaken && !options.force) return;
        const source = new DOMParser().parseFromString(text, 'text/html').querySelector('.drawer__inner');
        const target = document.querySelector('cart-drawer .drawer__inner');
        if (!source || !target) return;
        target.innerHTML = source.innerHTML;
        const empty = source.classList.contains('is-empty');
        target.classList.toggle('is-empty', empty);
        const host = document.querySelector('cart-drawer');
        if (host) {
          host.renderGeneration = (host.renderGeneration || 0) + 1;
          host.preparedRender = null;
          host.classList.toggle('is-empty', empty);
        }
      })
      .catch(() => {});
  }

  getSectionsToRender() {
    // Only the cart-drawer section is re-rendered on quantity/remove. The
    // header count badge (cart-icon-bubble) is not a section in this theme
    // and is synced independently by custom-header.liquid — requesting it
    // here returned empty HTML and threw inside the render loop.
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
